import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import HeaderTitle from "../../../components/common/HeaderTitle/HeaderTitle";
import DButton from "../../../components/Dashboard/DashboardButtons/Dbutton";
import PaginationComponent from "../../../components/common/Pagination/PaginationComponent";
import { inventoryApi } from "./inventoryApi";
import classes from "./InventoryMasterWarehouse.module.css";
import SearchBar from "../../../components/common/SearchBar/SearchBar";
import SuccessPopupDashboard from "../../../components/Popups/PopupFrame/SuccessPopupDashboard";
import successPopupImg from "../../../assets/images/png/Group 2 2@2x.png";
import errorPopupImg from "../../../assets/images/png/Group 2@2x.png";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Checkbox,
  Fade,
  Chip,
  FormControl,
  ListItemText,
  MenuItem,
  Modal,
  OutlinedInput,
  Select as MuiSelect,
  Tooltip,
} from "@mui/material";

const TABS = [
  { id: "warehouse", label: "Warehouse Master" },
  { id: "inventory", label: "Inventory Master" },
  { id: "grn", label: "GRN Approvals" },
  { id: "issue", label: "Material Issue" },
  { id: "movement", label: "Returns & Adjustments" },
  { id: "transfer", label: "Stock Transfer" },
  { id: "ledger", label: "Stock Ledger" },
  { id: "reports", label: "Consumption Reports" },
];

const today = () => new Date().toISOString().slice(0, 10);
const GRN_PAGE_SIZE = 10;
const INVENTORY_PAGE_SIZE = 10;
const TABLE_PAGE_SIZE = 10;
const DESCRIPTION_PREVIEW_LENGTH = 110;

const buildDefaultTablePagination = () => ({
  page_count: 1,
  total_count: 0,
  current_page_count: 0,
  current_page: 1,
  page_size: TABLE_PAGE_SIZE,
});

const buildDefaultTableState = () =>
  TABS.reduce((acc, tab) => {
    acc[tab.id] = {
      page: 1,
      search: "",
    };
    return acc;
  }, {});

const buildDefaultWarehouseForm = () => ({
  warehouse_code: "",
  warehouse_name: "",
  plant: "",
  store: "",
  location: "",
  store_incharge: "",
  status: "Active",
});

const buildDefaultInventoryForm = () => ({
  item_code: "",
  item_name: "",
  category: "",
  warehouse: "",
  current_stock: "0",
  reserved_stock: "0",
  available_stock: "0",
  batch: "",
  serial: "",
  reorder_level_percentage: "",
  seasonal_inventory_percentage: "",
  safety_stock_percentage: "",
  maximum_stock: "",
  inventory_value: "0",
  auto_reorder_mode: "",
  raiser_token: "",
  status: "Active",
});

const buildDefaultIssueForm = (approvedBy = "") => ({
  issue_no: "",
  date: today(),
  warehouse: "",
  issue_type: "DEPARTMENT",
  department: "",
  cost_centre: "",
  gl_code: "",
  employee: "",
  project: "",
  item_code: "",
  item_name: "",
  description: "",
  batch: "",
  serial: "",
  quantity: "",
  uom: "",
  approved_by: approvedBy,
  grn_token: "",
  remarks: "",
});

const buildDefaultMovementForm = () => ({
  transaction_type: "MATERIAL_RETURN",
  reference_no: "",
  txn_date: today(),
  warehouse: "",
  item_code: "",
  batch: "",
  serial: "",
  receipt_qty: "",
  issue_qty: "",
  physical_count: "",
  unit_cost: "",
});

const buildDefaultTransferForm = () => ({
  reference_no: "",
  txn_date: today(),
  source_warehouse: "",
  destination_warehouse: "",
  item_code: "",
  batch: "",
  serial: "",
  quantity: "",
});

const getErrorMessage = (error) => {
  const data = error?.response?.data;
  if (!data) return "Something went wrong. Please try again.";
  if (typeof data === "string") return data;
  if (data.error) return data.error;
  if (data.response) return data.response;
  const messages = Object.entries(data)
    .map(([field, value]) => {
      const label = field.replaceAll("_", " ");
      if (Array.isArray(value)) return `${label}: ${value.join(", ")}`;
      if (value && typeof value === "object") {
        return `${label}: ${JSON.stringify(value)}`;
      }
      return value ? `${label}: ${value}` : "";
    })
    .filter(Boolean);
  return messages.length ? messages.join("\n") : "Something went wrong.";
};

const getSuccessMessage = (data, fallback) => {
  if (typeof data === "string") return data;
  return data?.response || data?.message || data?.detail || fallback;
};

const escapeHtml = (value) =>
  String(value || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");

const formatPopupDescription = (value) =>
  escapeHtml(value).replace(/\n/g, "<br />");

const hasValue = (value) => String(value ?? "").trim() !== "";

const isPositiveNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0;
};

const isNonNegativeNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= 0;
};

const validateRequiredFields = (form, fields) =>
  fields
    .filter(({ field }) => !hasValue(form[field]))
    .map(({ label }) => `${label} is required.`);

const validatePercentage = (value, label, required = false) => {
  if (!hasValue(value)) return required ? `${label} is required.` : "";
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 0 || parsed > 100) {
    return `${label} must be between 0 and 100.`;
  }
  return "";
};

const toOption = (value, fallback = "-") =>
  value === undefined || value === null || value === ""
    ? fallback
    : typeof value === "object"
      ? JSON.stringify(value)
      : value;

const capitalizeValue = (value) => {
  if (value === undefined || value === null || value === "") return "-";
  return String(value)
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const normalizeNumber = (value) => {
  const parsed = Number(value || 0);
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatCurrencyValue = (value, currency = "INR") => {
  const amount = Number(value || 0);
  if (!Number.isFinite(amount)) return "-";
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: currency || "INR",
    maximumFractionDigits: 2,
  }).format(amount);
};

const normalizeInventoryDetailResponse = (data, itemCode) => {
  const fallbackDetail = {
    item_code: itemCode || "",
    item_count: 0,
    total_accepted_quantity: 0,
    total_value: 0,
    items: [],
  };

  const normalizeCode = (value) =>
    String(value || "")
      .trim()
      .toLowerCase();

  let source = data;
  if (typeof source === "string") {
    try {
      source = JSON.parse(source);
    } catch (error) {
      return fallbackDetail;
    }
  }

  source =
    source?.response ||
    source?.data ||
    source?.results ||
    source?.result ||
    source;

  source = source?.item_codes || source;

  if (!source || typeof source !== "object") {
    return fallbackDetail;
  }

  if (Array.isArray(source.items)) {
    return source;
  }

  const normalizedItemCode = normalizeCode(itemCode);
  const keyedDetail =
    Object.entries(source).find(
      ([key]) => normalizeCode(key) === normalizedItemCode,
    )?.[1] ||
    Object.values(source).find(
      (value) =>
        value &&
        typeof value === "object" &&
        normalizeCode(value.item_code) === normalizedItemCode,
    ) ||
    Object.values(source).find(
      (value) =>
        value &&
        typeof value === "object" &&
        Array.isArray(value.items) &&
        value.items.some(
          (item) => normalizeCode(item.item_code) === normalizedItemCode,
        ),
    ) ||
    Object.values(source).find(
      (value) =>
        value && typeof value === "object" && Array.isArray(value.items),
    );

  if (!keyedDetail || typeof keyedDetail !== "object") {
    return fallbackDetail;
  }

  if (Array.isArray(keyedDetail.items)) {
    return {
      item_code: keyedDetail.item_code || itemCode || "",
      item_count: keyedDetail.item_count ?? keyedDetail.items.length,
      total_accepted_quantity: keyedDetail.total_accepted_quantity ?? 0,
      total_available_quantity: keyedDetail.total_available_quantity ?? 0,
      total_value: keyedDetail.total_value ?? 0,
      items: keyedDetail.items.map((item) => ({
        ...item,
        total_available_quantity:
          item.total_available_quantity ?? keyedDetail.total_available_quantity ?? item.available_quantity ?? item.available_stock ?? 0,
      })),
    };
  }

  if (Array.isArray(keyedDetail)) {
    return {
      item_code: itemCode || "",
      item_count: keyedDetail.length,
      total_accepted_quantity: keyedDetail.reduce(
        (sum, item) => sum + Number(item?.accepted_quantity || 0),
        0,
      ),
      total_available_quantity: keyedDetail.reduce(
        (sum, item) =>
          sum +
          Number(
            item?.total_available_quantity ??
              item?.available_quantity ??
              item?.available_stock ??
              0,
          ),
        0,
      ),
      total_value: keyedDetail.reduce(
        (sum, item) => sum + Number(item?.total_price || 0),
        0,
      ),
      items: keyedDetail.map((item) => ({
        ...item,
        total_available_quantity:
          item?.total_available_quantity ?? item?.available_quantity ?? item?.available_stock ?? 0,
      })),
    };
  }

  return fallbackDetail;
};

const normalizeText = (value) =>
  String(value || "")
    .trim()
    .toLowerCase();

const getDepartmentName = (item) => {
  if (typeof item === "string") return item;
  return (
    item?.name ||
    item?.department ||
    item?.dept_name ||
    item?.department_name ||
    ""
  );
};

const isSameDepartment = (itemDepartment, selectedDepartment) => {
  if (!selectedDepartment) return true;
  return normalizeText(itemDepartment) === normalizeText(selectedDepartment);
};

const getInventoryKey = (row) =>
  [
    row?.warehouse,
    row?.item_code,
    row?.batch || "NO_BATCH",
    row?.serial || "NO_SERIAL",
  ].join("|");

const getRowKey = (row, index) =>
  row?.token ||
  row?.id ||
  row?.issue_no ||
  row?.reference_no ||
  row?.transfer_no ||
  row?.grn_token ||
  getInventoryKey(row) ||
  index;

const getInventoryDetailRowKey = (item, index) =>
  item?.grn_token ||
  `${item?.grn_number || "grn"}-${item?.item_code || "item"}-${index}`;

const getIssueStockItemKey = (item, index) =>
  item?.grn_token ||
  `${item?.grn_number || "grn"}-${item?.item_code || "item"}-${index}`;

const getIssueStockItemAvailableQty = (item) => {
  const accepted = Number(item?.accepted_quantity || 0);
  const rejected = Number(item?.rejected_quantity || 0);
  const available =
    item?.total_available_quantity ??
    item?.available_quantity ??
    item?.available_stock;
  if (available !== undefined && available !== null && available !== "") {
    return normalizeNumber(available);
  }
  return Math.max(accepted - rejected, 0);
};

const SelectField = ({
  label,
  value,
  onChange,
  options,
  required,
  disabled,
  className = "",
}) => (
  <label className={`${classes.field} ${className}`}>
    <span>
      {label}
      {required ? <b>*</b> : null}
    </span>
    <select
      value={value}
      disabled={disabled}
      onChange={(event) => onChange(event.target.value)}
    >
      <option value="">Select {label}</option>
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  </label>
);

const tooltipProps = {
  tooltip: {
    sx: {
      fontSize: "13px",
      fontFamily: "gilroyMedium",
      lineHeight: 1.45,
      maxWidth: "36rem",
    },
  },
};

const MultiSelectField = ({
  label,
  value,
  onChange,
  options,
  required,
  className = "",
}) => (
  <label className={`${classes.field} ${className}`}>
    <span>
      {label}
      {required ? <b>*</b> : null}
    </span>
    <FormControl fullWidth size="small" className={classes.itemMultiSelect}>
      <MuiSelect
        multiple
        value={value}
        onChange={(event) => {
          const nextValue = event.target.value;
          onChange(
            typeof nextValue === "string" ? nextValue.split(",") : nextValue,
          );
        }}
        input={<OutlinedInput />}
        renderValue={(selected) => {
          if (!selected.length) return "Select Item Name";
          return (
            <Box className={classes.selectedChipWrap}>
              {selected.map((selectedValue) => {
                const option = options.find(
                  (entry) => entry.value === selectedValue,
                );
                if (!option) return null;
                return (
                  <Tooltip
                    key={selectedValue}
                    title={option.description || option.name}
                    placement="top"
                    componentsProps={tooltipProps}
                  >
                    <Chip
                      label={option.name}
                      size="small"
                      className={classes.selectedChip}
                    />
                  </Tooltip>
                );
              })}
            </Box>
          );
        }}
        displayEmpty
        MenuProps={{
          PaperProps: {
            sx: {
              maxHeight: 320,
            },
          },
        }}
      >
        {options.map((option) => (
          <MenuItem
            key={option.value}
            value={option.value}
            className={classes.itemOption}
          >
            <Checkbox checked={value.includes(option.value)} />
            <Tooltip
              title={
                <div>
                  <div>{option.name}</div>
                  <div>{option.description || "-"}</div>
                </div>
              }
              placement="top"
              componentsProps={tooltipProps}
            >
              <ListItemText
                primary={option.name}
                secondary={option.meta}
                primaryTypographyProps={{
                  fontSize: 13,
                  fontFamily: "gilroySemiBold",
                }}
                secondaryTypographyProps={{
                  fontSize: 13,
                  noWrap: true,
                  maxWidth: 360,
                }}
              />
            </Tooltip>
          </MenuItem>
        ))}
      </MuiSelect>
    </FormControl>
  </label>
);

const REORDER_LEVEL_OPTIONS = [
  { value: "80", label: "80%" },
  { value: "70", label: "70%" },

  { value: "other", label: "Other" },
];

const SEASONAL_INVENTORY_OPTIONS = [
  { value: "5", label: "5%" },
  { value: "10", label: "10%" },
  { value: "other", label: "Other" },
];

const TextField = ({
  label,
  value,
  onChange,
  type = "text",
  required,
  disabled,
  className = "",
}) => (
  <label className={`${classes.field} ${className}`}>
    <span>
      {label}
      {required ? <b>*</b> : null}
    </span>
    <input
      type={type}
      value={value}
      disabled={disabled}
      onChange={(event) => onChange(event.target.value)}
      placeholder={`Enter ${label}`}
    />
  </label>
);

const DataGrid = ({
  columns,
  rows,
  loading,
  emptyMessage,
  actions,
  actionLabel = "Action",
}) => (
  <div className={classes.tableShell}>
    <table>
      <thead>
        <tr>
          {columns.map((column) => (
            <th key={column.field}>{column.label}</th>
          ))}
          {actions ? <th>{actionLabel}</th> : null}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, index) => (
          <tr key={getRowKey(row, index)}>
            {columns.map((column) => (
              <td key={column.field}>
                {toOption(
                  column.render ? column.render(row) : row[column.field],
                )}
              </td>
            ))}
            {actions ? <td>{actions(row)}</td> : null}
          </tr>
        ))}
      </tbody>
    </table>
    {loading ? <div className={classes.infoState}>Loading...</div> : null}
    {!loading && rows.length === 0 ? (
      <div className={classes.infoState}>
        {emptyMessage || "No records found"}
      </div>
    ) : null}
  </div>
);

const InventoryMasterWarehouse = () => {
  const loginData = useSelector((state) => state.loginData.value);
  const authToken = loginData?.auth_token || loginData?.token;
  const orgToken = loginData?.organization_token;
  const buyerToken = loginData?.buyer_token;
  const approvedByName =
    [loginData?.user?.first_name, loginData?.user?.last_name]
      .filter(Boolean)
      .join(" ") ||
    loginData?.user?.first_name ||
    loginData?.user?.username ||
    loginData?.user?.email ||
    "";
  const issuedBy =
    loginData?.user?.designation ||
    [loginData?.user?.first_name, loginData?.user?.last_name]
      .filter(Boolean)
      .join(" ") ||
    loginData?.user?.email ||
    "";

  const [activeTab, setActiveTab] = useState("warehouse");
  const [reportDimension, setReportDimension] = useState("department");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [savingAction, setSavingAction] = useState("");
  const [feedbackModal, setFeedbackModal] = useState({
    open: false,
    type: "success",
    title: "",
    description: "",
  });
  const [warehouses, setWarehouses] = useState([]);
  const [items, setItems] = useState([]);
  const [inventoryRows, setInventoryRows] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [costCentres, setCostCentres] = useState([]);
  const [glCodes, setGlCodes] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [inventoryUsers, setInventoryUsers] = useState([]);
  const [inventoryBuyers, setInventoryBuyers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [inventoryDetail, setInventoryDetail] = useState({
    open: false,
    row: null,
    data: null,
    loading: false,
    error: "",
  });
  const [expandedInventoryDescriptions, setExpandedInventoryDescriptions] =
    useState({});
  const [selectedIssueInventoryKey, setSelectedIssueInventoryKey] =
    useState("");
  const [issueStockItems, setIssueStockItems] = useState([]);
  const [selectedIssueStockItemKeys, setSelectedIssueStockItemKeys] = useState(
    [],
  );
  const [issueStockLoading, setIssueStockLoading] = useState(false);
  const [issueStockError, setIssueStockError] = useState("");
  const [tableQueriesByTab, setTableQueriesByTab] = useState(
    buildDefaultTableState,
  );
  const [tablePaginationByTab, setTablePaginationByTab] = useState(() =>
    TABS.reduce((acc, tab) => {
      acc[tab.id] = buildDefaultTablePagination();
      return acc;
    }, {}),
  );
  const currentTableQuery = tableQueriesByTab[activeTab] || {
    page: 1,
    search: "",
  };
  const currentTablePagination =
    tablePaginationByTab[activeTab] || buildDefaultTablePagination();
  const activeTabLabel = TABS.find((tab) => tab.id === activeTab)?.label || "";

  useEffect(() => {
    if (!approvedByName) return;
    setIssueForm((current) =>
      current.approved_by === approvedByName
        ? current
        : { ...current, approved_by: approvedByName },
    );
  }, [approvedByName]);

  const [warehouseForm, setWarehouseForm] = useState(buildDefaultWarehouseForm);

  const [inventoryForm, setInventoryForm] = useState(buildDefaultInventoryForm);
  const [isCustomReorderLevel, setIsCustomReorderLevel] = useState(false);
  const [isCustomSeasonalInventory, setIsCustomSeasonalInventory] =
    useState(false);

  const [issueForm, setIssueForm] = useState(() =>
    buildDefaultIssueForm(approvedByName),
  );

  const [movementForm, setMovementForm] = useState(buildDefaultMovementForm);

  const [transferForm, setTransferForm] = useState(buildDefaultTransferForm);

  const warehouseOptions = useMemo(
    () =>
      warehouses.map((warehouse) => ({
        value: warehouse.warehouse_code,
        label: `${warehouse.warehouse_code} - ${warehouse.warehouse_name || warehouse.location || "Warehouse"}`,
      })),
    [warehouses],
  );

  const itemOptions = useMemo(
    () =>
      items.map((item) => ({
        value: item.item_code,
        label: `${item.item_code} - ${item.item_name || "Item"}`,
      })),
    [items],
  );

  const inventoryItemOptions = useMemo(() => {
    const seenItemCodes = new Set();

    return inventoryRows.reduce((options, row) => {
      const itemCode = row?.item_code || "";
      if (!itemCode || seenItemCodes.has(itemCode)) return options;

      seenItemCodes.add(itemCode);
      options.push({
        value: itemCode,
        label: row?.item_name || itemCode,
      });
      return options;
    }, []);
  }, [inventoryRows]);

  const inventoryOptions = useMemo(
    () =>
      inventoryRows.map((row) => ({
        value: getInventoryKey(row),
        label: `${row.warehouse || "-"} | ${
          row.item_name ||
          items.find((item) => item.item_code === row.item_code)?.item_name ||
          row.item_code ||
          "Item"
        } | Avl ${row.available_stock ?? row.current_stock ?? 0}`,
      })),
    [inventoryRows, items],
  );

  const issueStockItemOptions = useMemo(
    () =>
      issueStockItems.map((item, index) => {
        const availableQty = getIssueStockItemAvailableQty(item);
        return {
          value: getIssueStockItemKey(item, index),
          name: item.item_name || item.item_code || "Item",
          description: item.description || "",
          meta: `GRN ${item.grn_number || "-"} | Avl ${availableQty} ${item.uom || ""}`.trim(),
        };
      }),
    [issueStockItems],
  );

  const departmentOptions = departments
    .map((item) => getDepartmentName(item))
    .filter(Boolean)
    .map((name) => ({
      value: name,
      label: name,
    }));

  const filteredCostCentres = costCentres.filter((item) =>
    isSameDepartment(
      item.department || item.dept_name || item.department_name,
      issueForm.department,
    ),
  );

  const filteredGlCodes = glCodes.filter((item) =>
    isSameDepartment(
      item.department || item.dept_name || item.department_name,
      issueForm.department,
    ),
  );

  const filteredProjects = projects.filter((item) =>
    isSameDepartment(
      item.department || item.dept_name || item.department_name,
      issueForm.department,
    ),
  );

  const costCentreOptions = filteredCostCentres.map((item) => ({
    value: item.costcenter_code,
    label: `${item.costcenter_code}${item.department ? ` - ${item.department}` : ""}`,
  }));

  const glCodeOptions = filteredGlCodes.map((item) => ({
    value: item.gl_code,
    label: `${item.gl_code}${item.department ? ` - ${item.department}` : ""}`,
  }));

  const employeeOptions = employees.map((item) => ({
    value: item.email || item.full_text,
    label: item.full_text || item.email,
  }));

  const inventoryUserOptions = inventoryUsers.map((user) => {
    const fullName = [user.first_name, user.last_name].filter(Boolean).join(" ");
    return {
      value: user.token,
      label: `${fullName || user.email || "User"}${user.email ? ` - ${user.email}` : ""}`,
    };
  });

  const inventoryBuyerOptions = inventoryBuyers.map((buyer) => ({
    value: buyer.token,
    label: `${buyer.buyer_name || buyer.full_name || buyer.name || buyer.email || "Buyer"}${buyer.email ? ` - ${buyer.email}` : ""}`,
  }));

  const inventoryRaiserOptions =
    inventoryForm.auto_reorder_mode === "AUTO_PO"
      ? inventoryBuyerOptions
      : inventoryUserOptions;

  const projectOptions = filteredProjects.map((item) => ({
    value: item.project_code,
    label: `${item.project_code}${item.department ? ` - ${item.department}` : ""}`,
  }));

  const updateForm = (setter) => (field, value) => {
    setter((current) => ({ ...current, [field]: value }));
  };

  const showFeedbackModal = useCallback((type, title, description) => {
    setFeedbackModal({
      open: true,
      type,
      title,
      description: formatPopupDescription(description),
    });
  }, []);

  const closeFeedbackModal = useCallback(() => {
    setFeedbackModal((current) => ({ ...current, open: false }));
  }, []);

  const showValidationErrors = useCallback(
    (errors) => {
      if (!errors.length) return false;
      showFeedbackModal(
        "error",
        "Please Review The Details",
        errors.join("\n"),
      );
      return true;
    },
    [showFeedbackModal],
  );

  const handleIssueDepartmentChange = (department) => {
    setIssueForm((current) => ({
      ...current,
      department,
      cost_centre: "",
      gl_code: "",
      project: "",
    }));
  };

  const applySelectedItem = (itemCode) => {
    const inventoryItem = inventoryRows.find(
      (entry) => entry.item_code === itemCode,
    );
    const item = items.find((entry) => entry.item_code === itemCode);
    setInventoryForm((current) => ({
      ...current,
      item_code: itemCode,
      item_name: inventoryItem?.item_name || item?.item_name || "",
      category: inventoryItem?.category || item?.category || "",
    }));
  };

  const handleReorderLevelChange = (value) => {
    if (value === "other") {
      setIsCustomReorderLevel(true);
      setInventoryForm((current) => ({
        ...current,
        reorder_level_percentage: "",
      }));
      return;
    }

    setIsCustomReorderLevel(false);
    setInventoryForm((current) => ({
      ...current,
      reorder_level_percentage: value,
    }));
  };

  const handleSeasonalInventoryChange = (value) => {
    if (value === "other") {
      setIsCustomSeasonalInventory(true);
      setInventoryForm((current) => ({
        ...current,
        seasonal_inventory_percentage: "",
      }));
      return;
    }

    setIsCustomSeasonalInventory(false);
    setInventoryForm((current) => ({
      ...current,
      seasonal_inventory_percentage: value,
    }));
  };

  const handleAutoReorderModeChange = (value) => {
    setInventoryForm((current) => ({
      ...current,
      auto_reorder_mode: value,
      raiser_token: "",
    }));
  };

  const handleIssueInventoryStockChange = async (key) => {
    setSelectedIssueInventoryKey(key);
    setSelectedIssueStockItemKeys([]);
    setIssueStockItems([]);
    setIssueStockError("");

    const selected = inventoryRows.find((row) => getInventoryKey(row) === key);
    if (!selected) {
      setIssueForm((current) => ({
        ...current,
        warehouse: "",
        item_code: "",
        item_name: "",
        grn_token: "",
        description: "",
        batch: "",
        serial: "",
        quantity: "",
        uom: "",
      }));
      return;
    }

    setIssueForm((current) => ({
      ...current,
      warehouse: selected.warehouse || "",
      item_code: selected.item_code || "",
      item_name:
        selected.item_name ||
        items.find((item) => item.item_code === selected.item_code)
          ?.item_name ||
        "",
      grn_token: selected.grn_token || "",
      description: "",
      batch: selected.batch || "",
      serial: selected.serial || "",
      quantity: "",
      uom: "",
    }));

    setIssueStockLoading(true);
    try {
      const data = await inventoryApi.getApprovedGrnItemsForInventory(
        authToken,
        {
          org_token: orgToken,
          item_code: selected.item_code || "",
        },
      );
      const detail = normalizeInventoryDetailResponse(
        data,
        selected.item_code || "",
      );
      setIssueStockItems(Array.isArray(detail.items) ? detail.items : []);
    } catch (error) {
      setIssueStockError(getErrorMessage(error));
    } finally {
      setIssueStockLoading(false);
    }
  };

  const handleIssueStockItemChange = (keys) => {
    setSelectedIssueStockItemKeys(keys);
    const selectedItems = issueStockItems.filter((item, index) =>
      keys.includes(getIssueStockItemKey(item, index)),
    );

    if (selectedItems.length === 0) {
      setIssueForm((current) => ({
        ...current,
        description: "",
        quantity: "",
        uom: "",
        grn_token: "",
      }));
      return;
    }

    const totalAvailableQty = selectedItems.reduce(
      (sum, item) => sum + getIssueStockItemAvailableQty(item),
      0,
    );
    const descriptions = selectedItems
      .map((item) => {
        const itemName = item.item_name || item.item_code || "Item";
        return `${itemName}: ${item.description || "-"}`;
      })
      .join("\n");
    const serials = selectedItems
      .map((item) => item.serial_number)
      .filter(Boolean)
      .join(", ");
    const batches = selectedItems
      .map((item) => item.batch)
      .filter(Boolean)
      .join(", ");
    const uoms = Array.from(
      new Set(selectedItems.map((item) => item.uom).filter(Boolean)),
    );

    setIssueForm((current) => ({
      ...current,
      item_code: selectedItems[0]?.item_code || current.item_code,
      item_name:
        selectedItems[0]?.item_name ||
        items.find((item) => item.item_code === selectedItems[0]?.item_code)
          ?.item_name ||
        current.item_name,
      grn_token: selectedItems[0]?.grn_token || current.grn_token,
      description: descriptions,
      batch: batches || current.batch,
      serial: serials || current.serial,
      quantity: totalAvailableQty ? String(totalAvailableQty) : "",
      uom: uoms.length === 1 ? uoms[0] : current.uom,
    }));
  };

  const applyInventorySelection = (key, target) => {
    const selected = inventoryRows.find((row) => getInventoryKey(row) === key);
    if (!selected) return;

    const patch = {
      warehouse: selected.warehouse || "",
      item_code: selected.item_code || "",
      batch: selected.batch || "",
      serial: selected.serial || "",
    };

    if (target === "issue") {
      const item = items.find(
        (entry) => entry.item_code === selected.item_code,
      );
      setIssueForm((current) => ({
        ...current,
        ...patch,
        item_name: selected.item_name || item?.item_name || current.item_name,
        grn_token: selected.grn_token || current.grn_token,
        description: item?.description || selected.item_name || "",
        uom: item?.unit_measure || current.uom,
      }));
      return;
    }

    setMovementForm((current) => ({ ...current, ...patch }));
  };

  const fetchReferenceData = useCallback(async () => {
    if (!authToken) return;

    const [warehouseData, itemData, inventoryData] = await Promise.all([
      inventoryApi.getWarehouseMaster(authToken, orgToken),
      orgToken
        ? inventoryApi.getItemMaster(authToken, orgToken)
        : Promise.resolve({ rows: [] }),
      inventoryApi.getInventoryMaster(authToken, orgToken, 1, 100),
    ]);

    setWarehouses(warehouseData.rows);
    setItems(itemData.rows);
    setInventoryRows(inventoryData.rows);
  }, [authToken, orgToken]);

  const fetchLookupData = useCallback(async () => {
    if (!authToken) return;

    const [
      departmentData,
      costCentreData,
      glCodeData,
      employeeData,
      inventoryUserData,
      projectData,
    ] = await Promise.all([
      orgToken
        ? inventoryApi.getDepartments(authToken, orgToken)
        : Promise.resolve({ rows: [] }),
      buyerToken
        ? inventoryApi.getCostCentres(authToken, buyerToken)
        : Promise.resolve({ rows: [] }),
      buyerToken
        ? inventoryApi.getGlCodes(authToken, buyerToken)
        : Promise.resolve({ rows: [] }),
      orgToken
        ? inventoryApi.getUsers(authToken, orgToken)
        : Promise.resolve({ rows: [] }),
      orgToken
        ? inventoryApi.getOnlyUsers(authToken, orgToken)
        : Promise.resolve({ rows: [] }),
      buyerToken
        ? inventoryApi.getProjects(authToken, buyerToken)
        : Promise.resolve({ rows: [] }),
    ]);

    setDepartments(departmentData.rows);
    setCostCentres(costCentreData.rows);
    setGlCodes(glCodeData.rows);
    setEmployees(employeeData.rows);
    setInventoryUsers(inventoryUserData.rows);
    setProjects(projectData.rows);
  }, [authToken, buyerToken, orgToken]);

  const fetchInventoryBuyers = useCallback(async () => {
    if (!authToken || !orgToken) return;

    const buyerData = await inventoryApi.getBuyers(authToken, orgToken);
    setInventoryBuyers(buyerData.rows);
  }, [authToken, orgToken]);

  const updateCurrentTableQuery = useCallback((patch) => {
    setTableQueriesByTab((current) => ({
      ...current,
      [activeTab]: {
        ...(current[activeTab] || { page: 1, search: "" }),
        ...patch,
      },
    }));
  }, [activeTab]);

  const fetchRows = useCallback(async () => {
    if (!authToken) return;

    const query = tableQueriesByTab[activeTab] || { page: 1, search: "" };
    const page = Number(query.page || 1);
    const search = String(query.search || "").trim();

    setLoading(true);
    setRows([]);
    setMessage({ type: "", text: "" });

    try {
      let result = { rows: [] };

      if (activeTab === "warehouse") {
        result = await inventoryApi.getWarehouseMaster(authToken, orgToken, {
          page,
          pageSize: TABLE_PAGE_SIZE,
          search,
        });
      } else if (activeTab === "inventory") {
        result = await inventoryApi.getInventoryMaster(
          authToken,
          orgToken,
          page || 1,
          INVENTORY_PAGE_SIZE,
          search,
        );
      } else if (activeTab === "grn") {
        result = await inventoryApi.getGrns(authToken, {
          org_token: orgToken,
          page: page || 1,
          page_size: GRN_PAGE_SIZE,
          search,
        });
      } else if (activeTab === "issue") {
        result = await inventoryApi.getMaterialIssues(authToken, orgToken, {
          page,
          pageSize: TABLE_PAGE_SIZE,
          search,
        });
      } else if (activeTab === "movement") {
        const ledger = await inventoryApi.getStockLedger(authToken, orgToken, {
          page,
          pageSize: TABLE_PAGE_SIZE,
          search,
        });
        result = {
          ...ledger,
          rows: ledger.rows.filter((row) =>
            [
              "MATERIAL_RETURN",
              "STOCK_ADJUSTMENT",
              "PHYSICAL_VERIFICATION",
            ].includes(row.transaction_type),
          ),
        };
      } else if (activeTab === "transfer") {
        const ledger = await inventoryApi.getStockLedger(authToken, orgToken, {
          page,
          pageSize: TABLE_PAGE_SIZE,
          search,
        });
        result = {
          ...ledger,
          rows: ledger.rows.filter(
            (row) => row.transaction_type === "STOCK_TRANSFER",
          ),
        };
      } else if (activeTab === "ledger") {
        result = await inventoryApi.getStockLedger(authToken, orgToken, {
          page,
          pageSize: TABLE_PAGE_SIZE,
          search,
        });
      } else if (activeTab === "reports") {
        result = await inventoryApi.getConsumptionReport(
          authToken,
          orgToken,
          reportDimension,
          {
            page,
            pageSize: TABLE_PAGE_SIZE,
            search,
          },
        );
      }

      setRows(result.rows);
      setTablePaginationByTab((current) => ({
        ...current,
        [activeTab]: result.pagination || buildDefaultTablePagination(),
      }));
    } catch (error) {
      setRows([]);
      setTablePaginationByTab((current) => ({
        ...current,
        [activeTab]: buildDefaultTablePagination(),
      }));
      setMessage({ type: "error", text: getErrorMessage(error) });
    } finally {
      setLoading(false);
    }
  }, [activeTab, authToken, orgToken, reportDimension, tableQueriesByTab]);

  useEffect(() => {
    fetchReferenceData().catch((error) => {
      setMessage({ type: "error", text: getErrorMessage(error) });
    });
  }, [fetchReferenceData]);

  useEffect(() => {
    if (activeTab === "inventory" || activeTab === "issue") {
      fetchLookupData().catch((error) => {
        setMessage({ type: "error", text: getErrorMessage(error) });
      });
    }
  }, [activeTab, fetchLookupData]);

  useEffect(() => {
    if (
      activeTab === "inventory" &&
      inventoryForm.auto_reorder_mode === "AUTO_PO" &&
      inventoryBuyers.length === 0
    ) {
      fetchInventoryBuyers().catch((error) => {
        setMessage({ type: "error", text: getErrorMessage(error) });
      });
    }
  }, [
    activeTab,
    fetchInventoryBuyers,
    inventoryBuyers.length,
    inventoryForm.auto_reorder_mode,
  ]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  useEffect(() => {
    const value = String(inventoryForm.reorder_level_percentage || "");
    if (
      value &&
      !REORDER_LEVEL_OPTIONS.some((option) => option.value === value)
    ) {
      setIsCustomReorderLevel(true);
    }
  }, [inventoryForm.reorder_level_percentage]);

  useEffect(() => {
    const value = String(inventoryForm.seasonal_inventory_percentage || "");
    if (
      value &&
      !SEASONAL_INVENTORY_OPTIONS.some((option) => option.value === value)
    ) {
      setIsCustomSeasonalInventory(true);
    }
  }, [inventoryForm.seasonal_inventory_percentage]);

  useEffect(() => {
    setMessage({ type: "", text: "" });
  }, [activeTab]);

  const refreshAll = useCallback(async () => {
    await fetchReferenceData();
    await fetchRows();
  }, [fetchReferenceData, fetchRows]);

  const handleTableSearchChange = useCallback(
    (value) => {
      updateCurrentTableQuery({ search: value, page: 1 });
    },
    [updateCurrentTableQuery],
  );

  const handleTablePageChange = useCallback(
    (value) => {
      if (value === "") {
        updateCurrentTableQuery({ page: "" });
        return;
      }
      const parsed = parseInt(value, 10);
      if (!Number.isNaN(parsed)) {
        updateCurrentTableQuery({ page: parsed });
      }
    },
    [updateCurrentTableQuery],
  );

  const goToPreviousTablePage = useCallback(() => {
    updateCurrentTableQuery({
      page: Math.max(1, Number(currentTableQuery.page || 1) - 1),
    });
  }, [currentTableQuery.page, updateCurrentTableQuery]);

  const goToNextTablePage = useCallback(() => {
    updateCurrentTableQuery({
      page: Math.min(
        currentTablePagination.page_count || 1,
        Number(currentTableQuery.page || 1) + 1,
      ),
    });
  }, [
    currentTablePagination.page_count,
    currentTableQuery.page,
    updateCurrentTableQuery,
  ]);

  const handleSaveWarehouse = async () => {
    const validationErrors = validateRequiredFields(warehouseForm, [
      { field: "warehouse_code", label: "Warehouse code" },
      { field: "warehouse_name", label: "Warehouse name" },
      { field: "location", label: "Location" },
      { field: "store_incharge", label: "Store incharge" },
    ]);

    if (showValidationErrors(validationErrors)) return;

    setSavingAction("warehouse");
    try {
      const response = await inventoryApi.saveWarehouse(authToken, {
        organization_token: orgToken,
        ...warehouseForm,
      });
      showFeedbackModal(
        "success",
        "Warehouse Saved",
        getSuccessMessage(response, "Warehouse master details saved successfully."),
      );
      setWarehouseForm(buildDefaultWarehouseForm());
      await refreshAll();
    } catch (error) {
      showFeedbackModal("error", "Warehouse Not Saved", getErrorMessage(error));
    } finally {
      setSavingAction("");
    }
  };

  const handleSaveInventory = async () => {
    const validationErrors = [
      ...validateRequiredFields(inventoryForm, [
        { field: "item_code", label: "Item" },
        { field: "warehouse", label: "Warehouse" },
        { field: "auto_reorder_mode", label: "Auto reorder" },
        { field: "raiser_token", label: "Raiser" },
        { field: "maximum_stock", label: "Maximum stock" },
        { field: "reorder_level_percentage", label: "Reorder level" },
      ]),
      hasValue(inventoryForm.maximum_stock) &&
      !isPositiveNumber(inventoryForm.maximum_stock)
        ? "Maximum stock must be greater than 0."
        : "",
      validatePercentage(
        inventoryForm.reorder_level_percentage,
        "Reorder level",
        false,
      ),
      validatePercentage(
        inventoryForm.seasonal_inventory_percentage,
        "Seasonal inventory",
        false,
      ),
      validatePercentage(
        inventoryForm.safety_stock_percentage,
        "Safety stock",
        false,
      ),
    ].filter(Boolean);

    if (showValidationErrors(validationErrors)) return;

    setSavingAction("inventory");
    try {
      const response = await inventoryApi.saveInventory(authToken, {
        organization_token: orgToken,
        warehouse: inventoryForm.warehouse,
        item_code: inventoryForm.item_code,
        maximum_stock: Number(inventoryForm.maximum_stock || 0),
        reorder_level_percentage: Number(
          inventoryForm.reorder_level_percentage || 0,
        ),
        seasonal_inventory_percentage: Number(
          inventoryForm.seasonal_inventory_percentage || 0,
        ),
        safety_stock_percentage: Number(
          inventoryForm.safety_stock_percentage || 0,
        ),
        auto_reorder_mode: inventoryForm.auto_reorder_mode,
        raiser_token: inventoryForm.raiser_token,
      });
      showFeedbackModal(
        "success",
        "Configuration Saved",
        getSuccessMessage(
          response,
          "Inventory configuration saved successfully.",
        ),
      );
      setInventoryForm(buildDefaultInventoryForm());
      setIsCustomReorderLevel(false);
      setIsCustomSeasonalInventory(false);
      await refreshAll();
    } catch (error) {
      showFeedbackModal(
        "error",
        "Configuration Not Saved",
        getErrorMessage(error),
      );
    } finally {
      setSavingAction("");
    }
  };

  const handleViewInventoryDetails = async (row) => {
    const itemCode =
      row?.item_code ||
      row?.itemCode ||
      row?.item ||
      row?.product_code ||
      row?.material_code ||
      "";
    setInventoryDetail({
      open: true,
      row,
      data: null,
      loading: true,
      error: "",
    });
    setExpandedInventoryDescriptions({});

    try {
      const data = await inventoryApi.getApprovedGrnItemsForInventory(
        authToken,
        {
          org_token: orgToken,
          item_code: itemCode,
        },
      );

      setInventoryDetail({
        open: true,
        row,
        data: normalizeInventoryDetailResponse(data, itemCode),
        loading: false,
        error: "",
      });
    } catch (error) {
      setInventoryDetail({
        open: true,
        row,
        data: null,
        loading: false,
        error: getErrorMessage(error),
      });
    }
  };

  const closeInventoryDetails = () => {
    setInventoryDetail({
      open: false,
      row: null,
      data: null,
      loading: false,
      error: "",
    });
    setExpandedInventoryDescriptions({});
  };

  const toggleInventoryDescription = (key) => {
    setExpandedInventoryDescriptions((current) => ({
      ...current,
      [key]: !current[key],
    }));
  };

  const handleCreateIssue = async () => {
    const validationErrors = [
      ...validateRequiredFields(issueForm, [
        { field: "issue_no", label: "Issue no" },
        { field: "date", label: "Date" },
        { field: "warehouse", label: "Warehouse" },
        { field: "issue_type", label: "Issue type" },
        { field: "item_code", label: "Item" },
        { field: "quantity", label: "Quantity" },
        { field: "uom", label: "UOM" },
        { field: "approved_by", label: "Approved by" },
      ]),
      selectedIssueStockItemKeys.length === 0
        ? "Select at least one GRN item stock."
        : "",
      !isPositiveNumber(issueForm.quantity)
        ? "Quantity must be greater than 0."
        : "",
      issueForm.issue_type === "DEPARTMENT" && !hasValue(issueForm.department)
        ? "Department is required for department issue."
        : "",
      issueForm.issue_type === "EMPLOYEE" && !hasValue(issueForm.employee)
        ? "Employee is required for employee issue."
        : "",
      issueForm.issue_type === "PROJECT" && !hasValue(issueForm.project)
        ? "Project is required for project issue."
        : "",
    ].filter(Boolean);

    if (showValidationErrors(validationErrors)) return;

    const itemName =
      issueForm.item_name ||
      issueStockItems.find((item, index) =>
        selectedIssueStockItemKeys.includes(getIssueStockItemKey(item, index)),
      )?.item_name ||
      inventoryRows.find(
        (row) => getInventoryKey(row) === selectedIssueInventoryKey,
      )?.item_name ||
      items.find((item) => item.item_code === issueForm.item_code)?.item_name ||
      "";
    const grnToken =
      issueForm.grn_token ||
      issueStockItems.find((item, index) =>
        selectedIssueStockItemKeys.includes(getIssueStockItemKey(item, index)),
      )?.grn_token ||
      "";

    setSavingAction("issue");
    try {
      const response = await inventoryApi.createMaterialIssue(authToken, {
        organization_token: orgToken,
        issue_no: issueForm.issue_no,
        date: issueForm.date,
        warehouse: issueForm.warehouse,
        issue_type: issueForm.issue_type,
        department: issueForm.department || null,
        cost_centre: issueForm.cost_centre || null,
        employee: issueForm.employee || null,
        project: issueForm.project || null,
        item_code: issueForm.item_code,
        item_name: itemName,
        grn_token: grnToken,
        description: issueForm.description,
        batch: issueForm.batch,
        serial: issueForm.serial,
        quantity: Number(issueForm.quantity || 0),
        uom: issueForm.uom,
        gl_code: issueForm.gl_code || null,
        issued_by: issuedBy,
        approved_by: issueForm.approved_by,
        remarks: issueForm.remarks,
      });
      showFeedbackModal(
        "success",
        "Material Issue Saved",
        getSuccessMessage(response, "Material issue saved successfully."),
      );
      setIssueForm(buildDefaultIssueForm(approvedByName));
      setSelectedIssueInventoryKey("");
      setSelectedIssueStockItemKeys([]);
      setIssueStockItems([]);
      setIssueStockError("");
      await refreshAll();
    } catch (error) {
      showFeedbackModal(
        "error",
        "Material Issue Not Saved",
        getErrorMessage(error),
      );
    } finally {
      setSavingAction("");
    }
  };

  const handleCreateMovement = async () => {
    const validationErrors = [
      ...validateRequiredFields(movementForm, [
        { field: "transaction_type", label: "Transaction type" },
        { field: "reference_no", label: "Reference no" },
        { field: "txn_date", label: "Transaction date" },
        { field: "warehouse", label: "Warehouse" },
        { field: "item_code", label: "Item" },
      ]),
      movementForm.transaction_type === "PHYSICAL_VERIFICATION" &&
      !isNonNegativeNumber(movementForm.physical_count)
        ? "Physical count must be 0 or more."
        : "",
      movementForm.transaction_type !== "PHYSICAL_VERIFICATION" &&
      !isPositiveNumber(movementForm.receipt_qty) &&
      !isPositiveNumber(movementForm.issue_qty)
        ? "Enter receipt quantity or issue quantity greater than 0."
        : "",
    ].filter(Boolean);

    if (showValidationErrors(validationErrors)) return;

    const payload = {
      organization_token: orgToken,
      transaction_type: movementForm.transaction_type,
      reference_no: movementForm.reference_no,
      warehouse: movementForm.warehouse,
      item_code: movementForm.item_code,
      batch: movementForm.batch,
      serial: movementForm.serial,
      txn_date: movementForm.txn_date,
    };
    if (payload.transaction_type === "PHYSICAL_VERIFICATION") {
      payload.physical_count = normalizeNumber(movementForm.physical_count);
    } else {
      payload.receipt_qty = normalizeNumber(movementForm.receipt_qty);
      payload.issue_qty = normalizeNumber(movementForm.issue_qty);
      if (movementForm.unit_cost !== "") {
        payload.unit_cost = normalizeNumber(movementForm.unit_cost);
      }
    }

    setSavingAction("movement");
    try {
      const response = await inventoryApi.createStockMovement(authToken, payload);
      showFeedbackModal(
        "success",
        "Stock Movement Saved",
        getSuccessMessage(response, "Stock movement saved successfully."),
      );
      setMovementForm(buildDefaultMovementForm());
      await refreshAll();
    } catch (error) {
      showFeedbackModal(
        "error",
        "Stock Movement Not Saved",
        getErrorMessage(error),
      );
    } finally {
      setSavingAction("");
    }
  };

  const handleCreateTransfer = async () => {
    const validationErrors = [
      ...validateRequiredFields(transferForm, [
        { field: "reference_no", label: "Reference no" },
        { field: "txn_date", label: "Transaction date" },
        { field: "source_warehouse", label: "Source warehouse" },
        { field: "destination_warehouse", label: "Destination warehouse" },
        { field: "item_code", label: "Item" },
        { field: "quantity", label: "Quantity" },
      ]),
      transferForm.source_warehouse &&
      transferForm.destination_warehouse &&
      transferForm.source_warehouse === transferForm.destination_warehouse
        ? "Source and destination warehouse must be different."
        : "",
      !isPositiveNumber(transferForm.quantity)
        ? "Quantity must be greater than 0."
        : "",
    ].filter(Boolean);

    if (showValidationErrors(validationErrors)) return;

    setSavingAction("transfer");
    try {
      const response = await inventoryApi.createStockTransfer(authToken, {
        organization_token: orgToken,
        reference_no: transferForm.reference_no,
        source_warehouse: transferForm.source_warehouse,
        destination_warehouse: transferForm.destination_warehouse,
        item_code: transferForm.item_code,
        quantity: normalizeNumber(transferForm.quantity),
        batch: transferForm.batch,
        serial: transferForm.serial,
        txn_date: transferForm.txn_date,
      });
      showFeedbackModal(
        "success",
        "Stock Transfer Saved",
        getSuccessMessage(response, "Stock transfer saved successfully."),
      );
      setTransferForm(buildDefaultTransferForm());
      await refreshAll();
    } catch (error) {
      showFeedbackModal(
        "error",
        "Stock Transfer Not Saved",
        getErrorMessage(error),
      );
    } finally {
      setSavingAction("");
    }
  };

  const columnsByTab = {
    warehouse: [
      { label: "Warehouse Code", field: "warehouse_code" },
      { label: "Warehouse Name", field: "warehouse_name" },
      { label: "Plant", field: "plant" },
      { label: "Store", field: "store" },
      { label: "Location", field: "location" },
      { label: "Store Incharge", field: "store_incharge" },
      { label: "Status", field: "status" },
    ],
    inventory: [
      { label: "Item Code", field: "item_code" },
      { label: "Item Name", field: "item_name" },
      { label: "Category", field: "category" },
      { label: "Warehouse", field: "warehouse" },
      { label: "Current Stock", field: "current_stock" },
      { label: "Available Stock", field: "available_stock" },
      { label: "Reserved Stock", field: "reserved_stock" },
      { label: "Batch", field: "batch" },
      { label: "Serial", field: "serial" },
      { label: "Reorder Level %", field: "reorder_level_percentage" },
      { label: "Reorder Quantity", field: "reorder_quantity" },
      { label: "Safety Stock %", field: "safety_stock_percentage" },
      { label: "Safety Stock Qty", field: "safety_stock_quantity" },
      { label: "Inventory Value", field: "inventory_value" },
      { label: "Status", field: "status" },
    ],
    grn: [
      { label: "GRN No", field: "grn_number" },
      { label: "Vendor", field: "vendor" },
      { label: "PO No", field: "purchase_order_no" },
      { label: "Item Code", field: "item_code" },
      { label: "Accepted Qty", field: "accepted_quantity" },
      { label: "Rejected Qty", field: "rejected_quantity" },
      { label: "Warehouse", field: "receiving_location" },
      {
        label: "Approval Status",
        field: "overall_approval_status",
        render: (row) => capitalizeValue(row.overall_approval_status),
      },
    ],
    issue: [
      { label: "Issue No", field: "issue_no" },
      { label: "Date", field: "date" },
      { label: "Warehouse", field: "warehouse" },
      { label: "Issue Type", field: "issue_type" },
      { label: "Department", field: "department" },
      { label: "Cost Centre", field: "cost_centre" },
      { label: "GL Code", field: "gl_code" },
      { label: "Employee", field: "employee" },
      { label: "Project", field: "project" },
      {
        label: "Item Name",
        field: "item_name",
        render: (row) =>
          row.item_name ||
          items.find((item) => item.item_code === row.item_code)?.item_name ||
          row.description ||
          row.item_code,
      },
      { label: "Quantity", field: "quantity" },
      { label: "UOM", field: "uom" },
      { label: "Approved By", field: "approved_by" },
      { label: "Status", field: "status" },
    ],
    movement: [
      { label: "Txn Date", field: "txn_date" },
      { label: "Reference No", field: "reference_no" },
      { label: "Transaction Type", field: "transaction_type" },
      { label: "Warehouse", field: "warehouse" },
      { label: "Item Code", field: "item_code" },
      { label: "Batch", field: "batch" },
      { label: "Serial", field: "serial" },
      { label: "Receipt Qty", field: "receipt_qty" },
      { label: "Issue Qty", field: "issue_qty" },
      { label: "Physical Count", field: "physical_count" },
      { label: "Unit Cost", field: "unit_cost" },
    ],
    transfer: [
      { label: "Txn Date", field: "txn_date" },
      { label: "Reference No", field: "reference_no" },
      { label: "Warehouse", field: "warehouse" },
      { label: "Item Code", field: "item_code" },
      { label: "Receipt Qty", field: "receipt_qty" },
      { label: "Issue Qty", field: "issue_qty" },
      { label: "Balance Qty", field: "balance_qty" },
      { label: "Unit Cost", field: "unit_cost" },
      { label: "Inventory Value", field: "inventory_value" },
      { label: "Source Module", field: "source_module" },
    ],
    ledger: [
      { label: "Txn Date", field: "txn_date" },
      { label: "Reference No", field: "reference_no" },
      { label: "Transaction Type", field: "transaction_type" },
      { label: "Warehouse", field: "warehouse" },
      { label: "Item Code", field: "item_code" },
      { label: "Receipt Qty", field: "receipt_qty" },
      { label: "Issue Qty", field: "issue_qty" },
      { label: "Balance Qty", field: "balance_qty" },
      { label: "Unit Cost", field: "unit_cost" },
      { label: "Inventory Value", field: "inventory_value" },
      { label: "Source Module", field: "source_module" },
    ],
    reports: [
      { label: "Dimension", field: reportDimension },
      { label: "Item Code", field: "item_code" },
      { label: "Quantity", field: "quantity" },
    ],
  };

  const tableColumns = columnsByTab[activeTab] || columnsByTab.inventory;

  return (
    <div className={classes.inventoryPage}>
      <div className={classes.topHeader}>
        <div>
          <HeaderTitle
            titleText="Inventory Master & Warehouse"
            customclass={classes.dashHead}
          />
          <p className={classes.pageIntro}>
            Configure warehouses, stock controls, issues, transfers, and ledger
            reports from one place.
          </p>
        </div>
      </div>

      <div className={classes.tabBar} role="tablist" aria-label="Inventory tabs">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={activeTab === tab.id}
            className={activeTab === tab.id ? classes.activeTab : ""}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {message.text ? (
        <div className={`${classes.message} ${classes[message.type]}`}>
          {message.text}
        </div>
      ) : null}

      {activeTab === "warehouse" ? (
        <section className={classes.panel}>
          <div className={classes.panelHeading}>
            <div>
              <h3>Warehouse Master</h3>
              <p>Keep warehouse codes, locations, and ownership accurate.</p>
            </div>
            <span>Required fields are marked *</span>
          </div>
          <div className={classes.formGrid}>
            {Object.keys(warehouseForm).map((field) =>
              field === "status" ? (
                <SelectField
                  key={field}
                  label="Status"
                  value={warehouseForm.status}
                  onChange={(value) =>
                    updateForm(setWarehouseForm)("status", value)
                  }
                  options={[
                    { value: "Active", label: "Active" },
                    { value: "Inactive", label: "Inactive" },
                  ]}
                />
              ) : (
                <TextField
                  key={field}
                  label={field.replaceAll("_", " ")}
                  value={warehouseForm[field]}
                  onChange={(value) =>
                    updateForm(setWarehouseForm)(field, value)
                  }
                  required={field === "warehouse_code"}
                />
              ),
            )}
          </div>
          <div className={classes.actions}>
            <DButton
              label="Save Warehouse"
              btnHeight="4rem"
              btnWidth="18rem"
              loadingState={savingAction === "warehouse"}
              onClick={handleSaveWarehouse}
            />
          </div>
        </section>
      ) : null}

      {activeTab === "inventory" ? (
        <section className={classes.panel}>
          <div className={classes.panelHeading}>
            <div>
              <h3>Inventory Configuration</h3>
              <p>
                Set reorder, safety, seasonal, and auto PR controls for each
                stock item.
              </p>
            </div>
            <span>Required fields are marked *</span>
          </div>
          <div className={classes.formGrid}>
            <SelectField
              label="Item"
              value={inventoryForm.item_code}
              onChange={applySelectedItem}
              options={inventoryItemOptions}
              required
            />
            <TextField
              label="Item Name"
              value={inventoryForm.item_name}
              onChange={(value) =>
                updateForm(setInventoryForm)("item_name", value)
              }
              disabled
            />
            <TextField
              label="Category"
              value={inventoryForm.category}
              onChange={(value) =>
                updateForm(setInventoryForm)("category", value)
              }
              disabled
            />
            <SelectField
              label="Warehouse"
              value={inventoryForm.warehouse}
              onChange={(value) =>
                updateForm(setInventoryForm)("warehouse", value)
              }
              options={warehouseOptions}
              required
            />
            <SelectField
              label="Auto Reorder"
              value={inventoryForm.auto_reorder_mode}
              onChange={handleAutoReorderModeChange}
              options={[
                { value: "AUTO_PR", label: "PR" },
                { value: "AUTO_PO", label: "PO" },
              ]}
              required
            />
            <SelectField
              label="Raiser"
              value={inventoryForm.raiser_token}
              onChange={(value) =>
                updateForm(setInventoryForm)("raiser_token", value)
              }
              options={inventoryRaiserOptions}
              disabled={!inventoryForm.auto_reorder_mode}
              required
            />
            <TextField
              label="maximum stock"
              value={inventoryForm.maximum_stock}
              onChange={(value) =>
                updateForm(setInventoryForm)("maximum_stock", value)
              }
              type="number"
            />
            <SelectField
              label="reorder level"
              value={
                isCustomReorderLevel
                  ? "other"
                  : inventoryForm.reorder_level_percentage
              }
              onChange={handleReorderLevelChange}
              options={REORDER_LEVEL_OPTIONS}
              className={classes.compactField}
            />
            {isCustomReorderLevel ? (
              <TextField
                label="custom reorder level"
                value={inventoryForm.reorder_level_percentage}
                onChange={(value) =>
                  updateForm(setInventoryForm)(
                    "reorder_level_percentage",
                    value,
                  )
                }
                type="number"
                className={classes.compactField}
              />
            ) : null}
            <SelectField
              label="seasonal inventory "
              value={
                isCustomSeasonalInventory
                  ? "other"
                  : inventoryForm.seasonal_inventory_percentage
              }
              onChange={handleSeasonalInventoryChange}
              options={SEASONAL_INVENTORY_OPTIONS}
              className={classes.compactField}
            />
            {isCustomSeasonalInventory ? (
              <TextField
                label="custom seasonal inventory "
                value={inventoryForm.seasonal_inventory_percentage}
                onChange={(value) =>
                  updateForm(setInventoryForm)(
                    "seasonal_inventory_percentage",
                    value,
                  )
                }
                type="number"
                className={classes.compactField}
              />
            ) : null}
            {["safety_stock_percentage"].map((field) => (
              <TextField
                key={field}
                label={field.replaceAll("_", " ")}
                value={inventoryForm[field]}
                onChange={(value) => updateForm(setInventoryForm)(field, value)}
                type={
                  field.includes("stock") ||
                  field.includes("percentage") ||
                  field.includes("value")
                    ? "number"
                    : "text"
                }
              />
            ))}
          </div>
          <div className={classes.actions}>
            <DButton
              label="Save Inventory Configuration"
              btnHeight="4rem"
              btnWidth="25rem"
              loadingState={savingAction === "inventory"}
              onClick={handleSaveInventory}
            />
          </div>
        </section>
      ) : null}

      {activeTab === "issue" ? (
        <section className={classes.panel}>
          <div className={classes.panelHeading}>
            <div>
              <h3>Material Issue</h3>
              <p>Select approved GRN stock, then issue it to the right owner.</p>
            </div>
            <span>Required fields are marked *</span>
          </div>
          <div className={classes.formGrid}>
            <SelectField
              label="Inventory Stock"
              value={selectedIssueInventoryKey}
              onChange={handleIssueInventoryStockChange}
              options={inventoryOptions}
            />
            {[
              ["issue_no", "Issue No"],
              ["date", "Date"],
            ].map(([field, label]) => (
              <TextField
                key={field}
                label={label}
                type={field === "date" ? "date" : "text"}
                value={issueForm[field]}
                onChange={(value) => updateForm(setIssueForm)(field, value)}
                required
              />
            ))}
            <SelectField
              label="Warehouse"
              value={issueForm.warehouse}
              onChange={(value) => updateForm(setIssueForm)("warehouse", value)}
              options={warehouseOptions}
              required
            />
            <SelectField
              label="Issue Type"
              value={issueForm.issue_type}
              onChange={(value) =>
                updateForm(setIssueForm)("issue_type", value)
              }
              options={[
                { value: "SHOP_FLOOR", label: "Shop Floor" },
                { value: "DEPARTMENT", label: "Department" },
                { value: "EMPLOYEE", label: "Employee" },
                { value: "PROJECT", label: "Project" },
              ]}
              required
            />
            <SelectField
              label="Department"
              value={issueForm.department}
              onChange={handleIssueDepartmentChange}
              options={departmentOptions}
            />
            <SelectField
              label="Cost Centre"
              value={issueForm.cost_centre}
              onChange={(value) =>
                updateForm(setIssueForm)("cost_centre", value)
              }
              options={costCentreOptions}
            />
            <SelectField
              label="GL Code"
              value={issueForm.gl_code}
              onChange={(value) => updateForm(setIssueForm)("gl_code", value)}
              options={glCodeOptions}
            />
            <SelectField
              label="Employee"
              value={issueForm.employee}
              onChange={(value) => updateForm(setIssueForm)("employee", value)}
              options={employeeOptions}
            />
            <SelectField
              label="Project"
              value={issueForm.project}
              onChange={(value) => updateForm(setIssueForm)("project", value)}
              options={projectOptions}
            />
            <MultiSelectField
              label="Item Name"
              value={selectedIssueStockItemKeys}
              onChange={handleIssueStockItemChange}
              options={issueStockItemOptions}
              required
              className={classes.wideField}
            />
            {issueStockLoading ? (
              <div className={classes.inlineInfo}>Loading item stock...</div>
            ) : null}
            {!issueStockLoading && issueStockError ? (
              <div className={classes.inlineError}>{issueStockError}</div>
            ) : null}
            {selectedIssueInventoryKey &&
            !issueStockLoading &&
            !issueStockError &&
            issueStockItemOptions.length === 0 ? (
              <div className={classes.inlineInfo}>
                No approved GRN item stock found.
              </div>
            ) : null}
            {[
              "batch",
              "serial",
              "quantity",
              "uom",
              "approved_by",
              "remarks",
            ].map((field) => (
              <TextField
                key={field}
                label={field.replaceAll("_", " ")}
                value={issueForm[field]}
                type={field === "quantity" ? "number" : "text"}
                onChange={(value) => updateForm(setIssueForm)(field, value)}
                required={field === "quantity"}
                disabled={field === "approved_by"}
              />
            ))}
          </div>
          <div className={classes.actions}>
            <DButton
              label="Save Material Issue"
              btnHeight="4rem"
              btnWidth="20rem"
              loadingState={savingAction === "issue"}
              onClick={handleCreateIssue}
            />
          </div>
        </section>
      ) : null}

      {activeTab === "movement" ? (
        <section className={classes.panel}>
          <div className={classes.panelHeading}>
            <div>
              <h3>Material Return, Stock Adjustment & Physical Verification</h3>
              <p>Post stock corrections with a clear reference and quantity.</p>
            </div>
            <span>Required fields are marked *</span>
          </div>
          <div className={classes.formGrid}>
            <SelectField
              label="Inventory Stock"
              value=""
              onChange={(value) => applyInventorySelection(value, "movement")}
              options={inventoryOptions}
            />
            <SelectField
              label="Transaction Type"
              value={movementForm.transaction_type}
              onChange={(value) =>
                updateForm(setMovementForm)("transaction_type", value)
              }
              options={[
                { value: "MATERIAL_RETURN", label: "Material Return" },
                { value: "STOCK_ADJUSTMENT", label: "Stock Adjustment" },
                {
                  value: "PHYSICAL_VERIFICATION",
                  label: "Physical Verification",
                },
              ]}
              required
            />
            {["reference_no", "txn_date"].map((field) => (
              <TextField
                key={field}
                label={field.replaceAll("_", " ")}
                type={field === "txn_date" ? "date" : "text"}
                value={movementForm[field]}
                onChange={(value) => updateForm(setMovementForm)(field, value)}
                required
              />
            ))}
            <SelectField
              label="Warehouse"
              value={movementForm.warehouse}
              onChange={(value) =>
                updateForm(setMovementForm)("warehouse", value)
              }
              options={warehouseOptions}
              required
            />
            <SelectField
              label="Item"
              value={movementForm.item_code}
              onChange={(value) =>
                updateForm(setMovementForm)("item_code", value)
              }
              options={itemOptions}
              required
            />
            {[
              "batch",
              "serial",
              "receipt_qty",
              "issue_qty",
              "physical_count",
              "unit_cost",
            ].map((field) => (
              <TextField
                key={field}
                label={field.replaceAll("_", " ")}
                value={movementForm[field]}
                type={
                  [
                    "receipt_qty",
                    "issue_qty",
                    "physical_count",
                    "unit_cost",
                  ].includes(field)
                    ? "number"
                    : "text"
                }
                onChange={(value) => updateForm(setMovementForm)(field, value)}
                disabled={
                  movementForm.transaction_type === "PHYSICAL_VERIFICATION"
                    ? ["receipt_qty", "issue_qty"].includes(field)
                    : field === "physical_count"
                }
              />
            ))}
          </div>
          <div className={classes.actions}>
            <DButton
              label="Save Stock Movement"
              btnHeight="4rem"
              btnWidth="20rem"
              loadingState={savingAction === "movement"}
              onClick={handleCreateMovement}
            />
          </div>
        </section>
      ) : null}

      {activeTab === "transfer" ? (
        <section className={classes.panel}>
          <div className={classes.panelHeading}>
            <div>
              <h3>Stock Transfer</h3>
              <p>Move stock between warehouses without losing audit context.</p>
            </div>
            <span>Required fields are marked *</span>
          </div>
          <div className={classes.formGrid}>
            {["reference_no", "txn_date"].map((field) => (
              <TextField
                key={field}
                label={field.replaceAll("_", " ")}
                type={field === "txn_date" ? "date" : "text"}
                value={transferForm[field]}
                onChange={(value) => updateForm(setTransferForm)(field, value)}
                required
              />
            ))}
            <SelectField
              label="Source Warehouse"
              value={transferForm.source_warehouse}
              onChange={(value) =>
                updateForm(setTransferForm)("source_warehouse", value)
              }
              options={warehouseOptions}
              required
            />
            <SelectField
              label="Destination Warehouse"
              value={transferForm.destination_warehouse}
              onChange={(value) =>
                updateForm(setTransferForm)("destination_warehouse", value)
              }
              options={warehouseOptions}
              required
            />
            <SelectField
              label="Item"
              value={transferForm.item_code}
              onChange={(value) =>
                updateForm(setTransferForm)("item_code", value)
              }
              options={itemOptions}
              required
            />
            {["batch", "serial", "quantity"].map((field) => (
              <TextField
                key={field}
                label={field}
                type={field === "quantity" ? "number" : "text"}
                value={transferForm[field]}
                onChange={(value) => updateForm(setTransferForm)(field, value)}
                required={field === "quantity"}
              />
            ))}
          </div>
          <div className={classes.actions}>
            <DButton
              label="Save Stock Transfer"
              btnHeight="4rem"
              btnWidth="19rem"
              loadingState={savingAction === "transfer"}
              onClick={handleCreateTransfer}
            />
          </div>
        </section>
      ) : null}

      <div className={classes.tableTools}>
        <div className={classes.filterGroup}>
          <div className={classes.tableTitle}>
            <strong>{activeTabLabel}</strong>
            <span>
              {currentTablePagination.total_count || 0} record
              {Number(currentTablePagination.total_count || 0) === 1
                ? ""
                : "s"}
            </span>
          </div>
          <SearchBar
            value={currentTableQuery.search || ""}
            onChange={handleTableSearchChange}
            placeholder={`Search ${activeTabLabel || "table"}`}
            customClass={classes.tableSearch}
          />
          {activeTab === "reports" ? (
            <SelectField
              label="Report"
              value={reportDimension}
              onChange={(value) => {
                setReportDimension(value || "department");
                updateCurrentTableQuery({ page: 1 });
              }}
              options={[
                { value: "department", label: "Department-wise" },
                { value: "project", label: "Project-wise" },
                { value: "employee", label: "Employee-wise" },
              ]}
            />
          ) : null}
        </div>
      </div>

      <DataGrid
        columns={tableColumns}
        rows={rows}
        loading={loading}
        actions={
          activeTab === "inventory"
            ? (row) => (
                <button
                  type="button"
                  className={classes.iconAction}
                  onClick={() => handleViewInventoryDetails(row)}
                  aria-label={`View stock details for ${row?.item_code || "item"}`}
                  title="View stock details"
                >
                  <VisibilityIcon fontSize="small" />
                </button>
              )
            : null
        }
        actionLabel="View"
      />
      {inventoryDetail.open ? (
        <div className={classes.modalOverlay} role="presentation">
          <div className={classes.detailModal} role="dialog" aria-modal="true">
            <div className={classes.modalHeader}>
              <div>
                <h3>Item Stock Details</h3>
                <p>
                  {inventoryDetail.row?.item_name || "-"} (
                  {inventoryDetail.row?.item_code || "-"})
                </p>
              </div>
              <button
                type="button"
                className={classes.closeButton}
                onClick={closeInventoryDetails}
                aria-label="Close stock details"
              >
                <CloseIcon fontSize="small" />
              </button>
            </div>

            {inventoryDetail.loading ? (
              <div className={classes.modalInfo}>Loading stock details...</div>
            ) : null}

            {!inventoryDetail.loading && inventoryDetail.error ? (
              <div className={classes.modalError}>{inventoryDetail.error}</div>
            ) : null}

            {!inventoryDetail.loading && !inventoryDetail.error ? (
              <>
                <div className={classes.summaryGrid}>
                  <div>
                    <span>Item Code</span>
                    <strong>{inventoryDetail.data?.item_code || "-"}</strong>
                  </div>
                  <div>
                    <span>GRN Item Count</span>
                    <strong>{inventoryDetail.data?.item_count ?? 0}</strong>
                  </div>
                  <div>
                    <span>Available Quantity</span>
                    <strong>
                      {inventoryDetail.data?.total_available_quantity ?? 0}
                    </strong>
                  </div>
                  <div>
                    <span>Total Value</span>
                    <strong>
                      {formatCurrencyValue(
                        inventoryDetail.data?.total_value,
                        inventoryDetail.data?.items?.[0]?.currency_type ||
                          "INR",
                      )}
                    </strong>
                  </div>
                </div>

                <div className={classes.detailTableShell}>
                  <table>
                    <thead>
                      <tr>
                        <th>GRN No</th>
                        <th>PO No</th>
                        <th>Vendor</th>
                        <th>Item Name</th>
                        <th>Description</th>
                        <th>Current Available Stock</th>
                        <th>Ordered Qty</th>
                        <th>Received Qty</th>
                        <th>Accepted Qty</th>
                        <th>Rejected Qty</th>
                        <th>UOM</th>
                        <th>Unit Price</th>
                        <th>Total Price</th>
                        <th>Warehouse</th>
                        <th>Condition</th>
                        <th>Serial No</th>
                        <th>Delivery Date</th>
                        <th>Created Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(inventoryDetail.data?.items || []).map(
                        (item, index) => {
                          const rowKey = getInventoryDetailRowKey(item, index);
                          const description = String(item.description || "");
                          const isLongDescription =
                            description.length > DESCRIPTION_PREVIEW_LENGTH;
                          const isExpanded =
                            !!expandedInventoryDescriptions[rowKey];
                          const visibleDescription =
                            isLongDescription && !isExpanded
                              ? `${description.slice(0, DESCRIPTION_PREVIEW_LENGTH)}...`
                              : description;

                          return (
                            <tr key={rowKey}>
                              <td className={classes.idCell}>
                                {toOption(item.grn_number)}
                              </td>
                              <td>{toOption(item.purchase_order_no)}</td>
                              <td>{toOption(item.vendor)}</td>
                              <td className={classes.itemNameCell}>
                                {toOption(item.item_name)}
                              </td>
                              <td className={classes.descriptionCell}>
                                <span>{toOption(visibleDescription)}</span>
                                {isLongDescription ? (
                                  <button
                                    type="button"
                                    className={classes.readMoreButton}
                                    onClick={() =>
                                      toggleInventoryDescription(rowKey)
                                    }
                                  >
                                    {isExpanded ? "Show less" : "Read more"}
                                  </button>
                                ) : null}
                              </td>

                              <td style={{ color: "blue", fontWeight: 700 }}>
                                {toOption(
                                  item.total_available_quantity ??
                                    item.available_quantity,
                                )}
                              </td>
                              <td>{toOption(item.quantity_ordered)}</td>
                              <td>{toOption(item.quantity_received)}</td>
                              <td className={classes.quantityGood}>
                                {toOption(item.accepted_quantity)}
                              </td>
                              <td>{toOption(item.rejected_quantity)}</td>
                              <td>{toOption(item.uom)}</td>
                              <td>
                                {formatCurrencyValue(
                                  item.unit_price,
                                  item.currency_type || "INR",
                                )}
                              </td>
                              <td className={classes.valueCell}>
                                {formatCurrencyValue(
                                  item.total_price,
                                  item.currency_type || "INR",
                                )}
                              </td>
                              <td>
                                {toOption(
                                  item.warehouse_name ||
                                    item.warehouse_code ||
                                    item.warehouse_location,
                                )}
                              </td>
                              <td>{toOption(item.condition)}</td>
                              <td>{toOption(item.serial_number)}</td>
                              <td>
                                {item.delivery_date
                                  ? new Date(
                                      item.delivery_date,
                                    ).toLocaleDateString("en-IN")
                                  : "-"}
                              </td>
                              <td>
                                {item.created_at
                                  ? new Date(
                                      item.created_at,
                                    ).toLocaleDateString("en-IN")
                                  : "-"}
                              </td>
                            </tr>
                          );
                        },
                      )}
                    </tbody>
                  </table>
                  {(inventoryDetail.data?.items || []).length === 0 ? (
                    <div className={classes.infoState}>
                      No approved GRN stock details found.
                    </div>
                  ) : null}
                </div>
              </>
            ) : null}
          </div>
        </div>
      ) : null}
      {(activeTab === "warehouse" ||
        activeTab === "inventory" ||
        activeTab === "grn" ||
        activeTab === "issue" ||
        activeTab === "movement" ||
        activeTab === "transfer" ||
        activeTab === "ledger" ||
        activeTab === "reports") ? (
        <PaginationComponent
          pageNumber={currentTableQuery.page || 1}
          setPageNumber={(value) => {
            handleTablePageChange(value);
          }}
          currentPage={currentTablePagination.current_page || currentTableQuery.page || 1}
          itemsPerPage={currentTablePagination.total_count || 0}
          totalItems={currentTablePagination.page_count || 0}
          previouPageClick={goToPreviousTablePage}
          nextPageClick={goToNextTablePage}
          disablePrevious={loading || Number(currentTableQuery.page || 1) <= 1}
          disableNext={
            loading ||
            Number(currentTableQuery.page || 1) >=
              (currentTablePagination.page_count || 1)
          }
          />
        ) : null}
      <Modal open={feedbackModal.open} onClose={closeFeedbackModal}>
        <Fade in={feedbackModal.open}>
          <Box>
            <SuccessPopupDashboard
              popupImage={
                feedbackModal.type === "success"
                  ? successPopupImg
                  : errorPopupImg
              }
              popupHeroText={feedbackModal.title}
              popupDescription={feedbackModal.description}
              btnText="OK"
              popBtn
              btnClick={closeFeedbackModal}
              buyerBtn
              customClass={classes.feedbackPopup}
            />
          </Box>
        </Fade>
      </Modal>
    </div>
  );
};

export default InventoryMasterWarehouse;
